/**
 * Product Bundle Configurator.
 *
 * PHP renders the visible HTML. JS only manages state, classes, filtering,
 * modal visibility, and AJAX submission.
 */

const icons = {
	check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
	plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
};

export function initializeProductBundle() {
	const root = document.querySelector('[data-dh-bundle]');
	if (!root || typeof window.dhBundleRuntime === 'undefined') return;
	const runtime = window.dhBundleRuntime;

	let payload;
	try {
		payload = JSON.parse(root.getAttribute('data-dh-bundle-payload'));
	} catch (err) {
		console.error('[dh-bundle] Invalid payload', err);
		return;
	}
	if (!payload || !Array.isArray(payload.groups) || payload.groups.length === 0) return;

	const i18nVault = root.querySelector('[data-dhb-i18n-vault]');
	const state = {
		selections: {},
		submitting: false,
		modalGroupIndex: null,
	};
	payload.groups.forEach((group) => { state.selections[group.index] = {}; });

	const notice = root.querySelector('[data-dh-bundle-notice]');
	const submitButton = root.querySelector('[data-dhb-submit]');
	let scrollLockY = 0;

	// iOS Safari: fixed overlays inside transformed/scroll ancestors size incorrectly.
	// Mount dialogs on <body> so inset:0 + dvh match the real viewport.
	(function mountModalsToBody() {
		let host = document.getElementById('dhb-modal-root');
		if (!host) {
			host = document.createElement('div');
			host.id = 'dhb-modal-root';
			document.body.appendChild(host);
		}
		Array.from(root.querySelectorAll('[data-dhb-modal]')).forEach((modal) => {
			host.appendChild(modal);
		});
	})();

	function lockBodyScroll() {
		scrollLockY = window.scrollY || window.pageYOffset || 0;
		document.body.classList.add('dhb-scroll-lock');
		document.body.style.position = 'fixed';
		document.body.style.top = `-${scrollLockY}px`;
		document.body.style.left = '0';
		document.body.style.right = '0';
		document.body.style.width = '100%';
	}

	function unlockBodyScroll() {
		document.body.classList.remove('dhb-scroll-lock');
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.left = '';
		document.body.style.right = '';
		document.body.style.width = '';
		window.scrollTo(0, scrollLockY);
	}

	function t(key) {
		const node = i18nVault && i18nVault.querySelector(`[data-dhb-i18n="${key}"]`);
		return node ? node.textContent.trim() : '';
	}

	function fmt(template, ...args) {
		let out = template || '';
		args.forEach((val, i) => {
			const n = i + 1;
			out = out.split(`%${n}$s`).join(String(val));
			out = out.split(`%${n}$d`).join(String(val));
		});
		return out;
	}

	function shortTitle(raw) {
		let s = String(raw || '').trim();
		s = s.replace(/^w[aä]hle\s+/i, '');
		s = s.replace(/^(dein(e)?|mein(e)?|ein(e)?)\s+/i, '');
		s = s.replace(/^\d+\s+(verschiedene?\s+)?/i, '');
		s = s.replace(/\s+w[aä]hlen?\s*$/i, '');
		return s.trim() || String(raw || '').trim();
	}

	function groupNode(group) {
		return root.querySelector(`[data-dhb-group="${group.index}"]`);
	}

	function modalNode(group) {
		return document.querySelector(`[data-dhb-modal="${group.index}"]`);
	}

	function optionFor(group, optionIndex) {
		return group.options.find((option) => String(option.index) === String(optionIndex));
	}

	function optionImageFromModal(group, optionIndex) {
		const modal = modalNode(group);
		if (!modal) return '';
		const row = modal.querySelector(`[data-option-index="${String(optionIndex)}"]`);
		const img = row?.querySelector('img');
		return img?.getAttribute('data-src') || img?.getAttribute('src') || '';
	}

	function applyImageToContainer(container, image) {
		if (!container) return;
		const img = container.querySelector('img');
		if (!img) return;

		const next = image || '';
		img.setAttribute('src', next);
		img.setAttribute('data-src', next);

		const picture = img.closest('picture');
		if (picture) {
			picture.querySelectorAll('source').forEach((source) => {
				source.setAttribute('srcset', next);
				source.setAttribute('data-srcset', next);
			});
		}
	}

	function unitsInGroup(group) {
		const sel = state.selections[group.index] || {};
		return Object.keys(sel).reduce((sum, oi) => sum + (sel[oi] || 0), 0);
	}

	function groupComplete(group) {
		return unitsInGroup(group) === group.requiredQuantity;
	}

	function allComplete() {
		return payload.groups.every(groupComplete);
	}

	function listSelections(group) {
		const sel = state.selections[group.index] || {};
		return Object.keys(sel).reduce((out, oi) => {
			const qty = sel[oi] || 0;
			const option = optionFor(group, oi);
			if (qty > 0 && option) out.push({ option, qty });
			return out;
		}, []);
	}

	function expandToUnits(group) {
		const units = [];
		listSelections(group).forEach(({ option, qty }) => {
			for (let i = 0; i < qty; i++) units.push(option);
		});
		return units;
	}

	function setSelection(groupIndex, optionIndex, qty) {
		state.selections[groupIndex] = state.selections[groupIndex] || {};
		if (qty <= 0) delete state.selections[groupIndex][optionIndex];
		else state.selections[groupIndex][optionIndex] = qty;
	}

	function pickOneOf(group, option) {
		if (!option.isInStock) return;
		state.selections[group.index] = { [option.index]: group.requiredQuantity };
	}

	function bumpAnyOf(group, option, delta) {
		if (!option.isInStock && delta > 0) return;
		const current = (state.selections[group.index] || {})[option.index] || 0;
		const total = unitsInGroup(group);
		let next = Math.max(0, current + delta);
		if (delta > 0 && total + delta > group.requiredQuantity) {
			next = current + Math.max(0, group.requiredQuantity - total);
		}
		setSelection(group.index, option.index, next);
	}

	function togglePodRow(group, option) {
		if (!option.isInStock) return;
		const current = (state.selections[group.index] || {})[option.index] || 0;
		const atCap = unitsInGroup(group) >= group.requiredQuantity && current === 0;
		if (current > 0) setSelection(group.index, option.index, 0);
		else if (!atCap) bumpAnyOf(group, option, 1);
		updateUI();
	}

	function gateMessage() {
		const incomplete = payload.groups.find((group) => !groupComplete(group));
		if (!incomplete) return '';
		const remaining = Math.max(0, incomplete.requiredQuantity - unitsInGroup(incomplete));
		return pickMessage(incomplete, remaining);
	}

	function pickMessage(group, remaining) {
		const title = shortTitle(group.title);
		if (remaining <= 1) return fmt(t('cta_pick_single_title'), title);
		return fmt(t('cta_pick_remaining_title'), remaining, title);
	}

	function showNotice(message, kind) {
		if (!notice) return;
		notice.hidden = false;
		notice.textContent = message;
		notice.className = 'dhb__notice is-' + (kind || 'info');
	}

	function clearNotice() {
		if (!notice) return;
		notice.hidden = true;
		notice.textContent = '';
		notice.className = 'dhb__notice';
	}

	function updateCounts(group) {
		const section = groupNode(group);
		const count = section?.querySelector('[data-dhb-count]');
		const complete = groupComplete(group);
		if (count) {
			count.textContent = `${unitsInGroup(group)} / ${group.requiredQuantity}`;
			count.classList.toggle('is-complete', complete);
		}

		if (section) {
			section.setAttribute(
				'aria-label',
				complete
					? fmt(t('aria_change'), shortTitle(group.title))
					: fmt(t('aria_pick'), shortTitle(group.title))
			);
		}

		const action = section?.querySelector('.dhb-section__add');
		const label = action?.querySelector('[data-dhb-section-add-label]');
		const plusIcon = action?.querySelector('[data-dhb-section-add-icon="plus"]');
		const editIcon = action?.querySelector('[data-dhb-section-add-icon="edit"]');
		if (label) label.textContent = complete ? 'Ändern' : 'Hinzufügen';
		if (plusIcon) plusIcon.hidden = complete;
		if (editIcon) editIcon.hidden = !complete;
	}

	function updateKitSlot(group) {
		const pick = listSelections(group)[0]?.option || null;
		const slot = groupNode(group)?.querySelector('[data-dhb-kit-slot]');
		if (!slot) return;
		slot.classList.toggle('is-filled', !!pick);
		slot.classList.toggle('is-empty', !pick);

		const plus = slot.querySelector('[data-dhb-kit-plus]');
		const thumb = slot.querySelector('[data-dhb-kit-thumb]');
		const img = thumb?.querySelector('img');
		const copy = slot.querySelector('[data-dhb-kit-copy]');
		const title = slot.querySelector('[data-dhb-kit-title]');
		const check = slot.querySelector('[data-dhb-kit-check]');
		const action = slot.querySelector('[data-dhb-kit-action]');
		if (plus) plus.hidden = !!pick;
		if (copy) copy.hidden = true;
		if (check) check.hidden = true;
		if (action) action.hidden = true;
		if (title) title.textContent = pick ? pick.title : '';
		if (img) {
			const baseImage = thumb?.getAttribute('data-base-image') || '';
			const image = pick ? (optionImageFromModal(group, pick.index) || pick.image || baseImage) : baseImage;
			applyImageToContainer(thumb, image);
			if (thumb) thumb.hidden = !image;
		}
	}

	function updateKitModal(group) {
		modalNode(group)?.querySelectorAll('[data-dhb-kit-option]').forEach((row) => {
			const option = optionFor(group, row.getAttribute('data-option-index'));
			const selected = !!option && ((state.selections[group.index] || {})[option.index] || 0) > 0;
			row.classList.toggle('is-selected', selected);
			row.querySelector('.dhb-kit-option__radio')?.classList.toggle('is-selected', selected);
		});
	}

	function updatePodSlots(group) {
		const units = expandToUnits(group);
		const section = groupNode(group);
		const picker = section?.querySelector('[data-dhb-pod-picker]');
		const hasSelection = units.length > 0;
		if (picker) {
			picker.classList.toggle('is-filled', hasSelection);
			picker.classList.toggle('is-empty', !hasSelection);
		}
		section?.querySelectorAll('[data-dhb-pod-slot]').forEach((slot) => {
			const index = Number(slot.getAttribute('data-slot-index'));
			const option = units[index] || null;
			const filled = !!option;
			slot.classList.toggle('is-filled', filled);
			slot.classList.toggle('is-empty', !filled);
			const thumb = slot.querySelector('[data-dhb-pod-thumb]');
			const img = thumb?.querySelector('img');
			const baseImage = thumb?.getAttribute('data-base-image') || '';
			const image = filled ? (optionImageFromModal(group, option?.index) || option?.image || baseImage) : baseImage;
			if (thumb) thumb.hidden = !image;
			if (img) {
				applyImageToContainer(thumb, image);
				img.alt = '';
			}
		});
	}

	function applyPodSearch(group) {
		const modal = modalNode(group);
		if (!modal) return;
		const input = modal.querySelector('[data-dhb-pod-search]');
		const clear = modal.querySelector('[data-dhb-pod-search-clear]');
		const empty = modal.querySelector('[data-dhb-empty-results]');
		const query = (input?.value || '').trim().toLocaleLowerCase();
		let visible = 0;
		modal.querySelectorAll('[data-dhb-pod-option]').forEach((row) => {
			const haystack = (row.getAttribute('data-search') || row.textContent || '').toLocaleLowerCase();
			const match = !query || haystack.includes(query);
			row.hidden = !match;
			if (match) visible++;
		});
		if (clear) clear.hidden = !query;
		if (empty) {
			empty.hidden = visible !== 0;
			empty.textContent = query ? fmt(t('search_no_results'), input.value.trim()) : '';
		}
	}

	function updatePodModal(group) {
		const modal = modalNode(group);
		if (!modal) return;
		const total = unitsInGroup(group);
		const cap = group.requiredQuantity;
		modal.querySelectorAll('[data-dhb-pod-option]').forEach((row) => {
			const option = optionFor(group, row.getAttribute('data-option-index'));
			if (!option) return;
			const qty = (state.selections[group.index] || {})[option.index] || 0;
			const atCap = total >= cap && qty === 0;
			row.classList.toggle('is-selected', qty > 0);
			row.classList.toggle('is-at-cap', atCap);
			row.querySelector('[data-dhb-qty-val]').textContent = String(qty);
			row.querySelector('[data-dhb-qty-minus]').disabled = qty <= 0;
			row.querySelector('[data-dhb-qty-plus]').disabled = !option.isInStock || atCap;
		});

		const units = expandToUnits(group);
		const stripCount = modal.querySelector('[data-dhb-strip-count]');
		if (stripCount) stripCount.textContent = `${units.length} / ${cap}`;
		modal.querySelectorAll('[data-dhb-footer-slot]').forEach((cell) => {
			const option = units[Number(cell.getAttribute('data-slot-index'))] || null;
			const imgWrap = cell.querySelector('[data-dhb-footer-img]');
			const img = imgWrap?.querySelector('img');
			const baseImage = imgWrap?.getAttribute('data-base-image') || '';
			const image = option ? (optionImageFromModal(group, option.index) || option.image || baseImage) : baseImage;
			cell.classList.toggle('is-filled', !!option);
			if (imgWrap) imgWrap.hidden = !image;
			if (img) applyImageToContainer(imgWrap, image);
		});

		const short = cap - units.length;
		const ready = short === 0;
		const done = modal.querySelector('[data-dhb-modal-done]');
		if (done) {
			done.disabled = !ready;
			done.className = 'dhb-modal__done ' + (ready ? 'is-ready' : 'is-gated');
			if (ready) {
				const title = shortTitle(group.title);
				done.innerHTML = `${cap === 1 ? t('take_over_single') : fmt(t('take_over_many'), cap, title)} <span class="dhb-icon">${icons.check}</span>`;
			} else {
				done.textContent = pickMessage(group, short);
			}
		}
		applyPodSearch(group);
	}

	function updateCTA() {
		const ready = allComplete();
		if (!submitButton) return;
		submitButton.classList.toggle('is-ready', ready);
		submitButton.classList.toggle('is-gated', !ready);
		submitButton.classList.toggle('loading', !!state.submitting);
		submitButton.disabled = !ready || state.submitting;
		const submitLabel = submitButton.getAttribute('data-dhb-submit-label') || t('cta_add_cart');
		submitButton.textContent = ready ? submitLabel : gateMessage();
	}

	function updateUI() {
		payload.groups.forEach((group) => {
			updateCounts(group);
			if (group.selectionType === 'one_of') {
				updateKitSlot(group);
				updateKitModal(group);
			} else {
				updatePodSlots(group);
				updatePodModal(group);
			}
		});
		updateCTA();
	}

	function openModal(group) {
		state.modalGroupIndex = group.index;
		document.querySelectorAll('[data-dhb-modal]').forEach((modal) => { modal.hidden = true; });
		const modal = modalNode(group);
		if (modal) {
			modal.hidden = false;
			lockBodyScroll();
			if (group.selectionType === 'any_of') {
				const input = modal.querySelector('[data-dhb-pod-search]');
				if (input) input.value = '';
				applyPodSearch(group);
			}
		}
		updateUI();
	}

	function closeModal() {
		state.modalGroupIndex = null;
		document.querySelectorAll('[data-dhb-modal]').forEach((modal) => { modal.hidden = true; });
		unlockBodyScroll();
		updateUI();
	}

	async function submit(originButton) {
		if (state.submitting || !allComplete()) return;
		state.submitting = true;
		clearNotice();
		updateUI();

		const body = new FormData();
		body.append('action', 'dh_bundle_add');
		body.append('nonce', runtime.nonce);
		body.append('bundle_id', payload.productId);
		const sels = [];
		payload.groups.forEach((group) => {
			const sel = state.selections[group.index] || {};
			Object.keys(sel).forEach((oi) => {
				sels.push({ group_index: group.index, option_index: parseInt(oi, 10), qty: sel[oi] });
			});
		});
		body.append('selections', JSON.stringify(sels));

		try {
			const res = await fetch(runtime.ajaxUrl, { method: 'POST', credentials: 'same-origin', body });
			const json = await res.json();
			if (!json || !json.success) {
				showNotice((json && json.data && json.data.message) || t('error_add_failed'), 'error');
				return;
			}
			const data = json.data || {};
			clearNotice();
			if (data.fragments && window.jQuery) {
				window.jQuery(document.body).trigger('added_to_cart', [data.fragments, data.cart_hash || '', window.jQuery(originButton)]);
			} else if (data.fragments) {
				Object.keys(data.fragments).forEach((sel) => {
					const node = document.querySelector(sel);
					if (node) node.outerHTML = data.fragments[sel];
				});
			}
		} catch (err) {
			showNotice(t('error_network'), 'error');
		} finally {
			state.submitting = false;
			updateUI();
		}
	}

	root.querySelectorAll('[data-dhb-group]').forEach((section) => {
		const openGroupModal = () => {
			const group = payload.groups.find((g) => String(g.index) === section.getAttribute('data-dhb-group'));
			if (group) openModal(group);
		};
		section.addEventListener('click', openGroupModal);
		section.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				openGroupModal();
			}
		});
	});

	document.querySelectorAll('[data-dhb-modal]').forEach((modal) => {
		modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
		modal.querySelector('.dhb-modal__sheet')?.addEventListener('click', (e) => e.stopPropagation());
	});

	document.querySelectorAll('[data-dhb-close-modal], [data-dhb-modal-done]').forEach((node) => {
		node.addEventListener('click', closeModal);
	});

	document.querySelectorAll('[data-dhb-kit-option]').forEach((row) => {
		row.addEventListener('click', () => {
			const group = payload.groups.find((g) => String(g.index) === row.getAttribute('data-group-index'));
			const option = group && optionFor(group, row.getAttribute('data-option-index'));
			if (!group || !option || !option.isInStock) return;
			pickOneOf(group, option);
			updateUI();
			setTimeout(closeModal, 180);
		});
	});

	document.querySelectorAll('[data-dhb-pod-option]').forEach((row) => {
		const group = payload.groups.find((g) => String(g.index) === row.getAttribute('data-group-index'));
		const option = group && optionFor(group, row.getAttribute('data-option-index'));
		row.addEventListener('click', () => { if (group && option) togglePodRow(group, option); });
		row.querySelector('[data-dhb-qty-minus]')?.addEventListener('click', (e) => {
			e.stopPropagation();
			if (group && option) {
				bumpAnyOf(group, option, -1);
				updateUI();
			}
		});
		row.querySelector('[data-dhb-qty-plus]')?.addEventListener('click', (e) => {
			e.stopPropagation();
			if (group && option) {
				bumpAnyOf(group, option, 1);
				updateUI();
			}
		});
	});

	document.querySelectorAll('[data-dhb-pod-search]').forEach((input) => {
		input.addEventListener('input', () => {
			const modal = input.closest('[data-dhb-modal]');
			const group = payload.groups.find((g) => String(g.index) === modal?.getAttribute('data-dhb-modal'));
			if (group) applyPodSearch(group);
		});
	});

	document.querySelectorAll('[data-dhb-pod-search-clear]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const modal = btn.closest('[data-dhb-modal]');
			const input = modal?.querySelector('[data-dhb-pod-search]');
			if (input) input.value = '';
			const group = payload.groups.find((g) => String(g.index) === modal?.getAttribute('data-dhb-modal'));
			if (group) applyPodSearch(group);
		});
	});

	if (submitButton) submitButton.addEventListener('click', (e) => submit(e.currentTarget));
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && state.modalGroupIndex != null) closeModal();
	});

	updateUI();
}
